import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Check, ExternalLink, Instagram, Mail, MapPin, Phone, Star } from 'lucide-react';
import SEO from '../components/SEO';
import PartnerCard from '../components/partners/PartnerCard';
import PartnerEnquiryForm from '../components/partners/PartnerEnquiryForm';
import PartnerGallery from '../components/partners/PartnerGallery';
import { partners, getPartnerBySlug, getPartnerSlug } from '../data/partners';
import { getHighlights } from '../data/partnerHighlights';

const API = process.env.REACT_APP_BACKEND_URL;

const useFeaturedWeddings = (name) => {
  const [weddings, setWeddings] = useState([]);
  useEffect(() => {
    axios
      .get(`${API}/api/weddings`)
      .then((res) => setWeddings(res.data.filter((w) => (w.full_story || '').includes(name))))
      .catch((err) => console.error('Failed to load weddings:', err));
  }, [name]);
  return weddings;
};

export default function PartnerDetailPage() {
  const { slug } = useParams();
  const partner = getPartnerBySlug(slug);
  const weddings = useFeaturedWeddings(partner?.name || '');

  const gallery = useMemo(() => {
    if (!partner) return [];
    const fromWeddings = weddings.flatMap((w) => w.gallery || []);
    return [...new Set([...(partner.gallery || []), partner.image, ...fromWeddings])].slice(0, partner.gallery ? 10 : 7);
  }, [partner, weddings]);

  const related = useMemo(
    () => (partner ? partners.filter((p) => p.category === partner.category && p.id !== partner.id).slice(0, 3) : []),
    [partner]
  );

  if (!partner) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" style={{ backgroundColor: '#FAF9F7' }} data-testid="partner-not-found">
        <h1 className="text-4xl mb-4" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>Partner not found</h1>
        <Link to="/partners" className="text-sm underline" style={{ color: '#DE92A9' }}>Back to all partners</Link>
      </div>
    );
  }

  const highlights = getHighlights(partner.category);
  const hasWebsite = partner.website && partner.website !== '#';
  const hasInstagram = partner.instagram && partner.instagram !== '#';

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="partner-detail-page">
      <SEO
        title={`${partner.name} | Wedding ${partner.category} in Malta`}
        description={partner.description}
        image={partner.image}
        keywords={`${partner.name}, wedding ${partner.category.toLowerCase()} malta, ${partner.location}, malta wedding suppliers`}
        breadcrumbs={[{ name: 'Our Partners', url: '/partners' }, { name: partner.name, url: `/partners/${slug}` }]}
      />

      <section className="relative h-[380px] md:h-[480px] overflow-hidden">
        <img src={partner.image} alt={partner.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-6xl mx-auto w-full px-6 lg:px-8 pb-10 md:pb-14">
            <Link to="/partners" className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-xs tracking-[0.2em] mb-6 transition-colors" data-testid="partner-back-link">
              <ArrowLeft size={14} /> ALL PARTNERS
            </Link>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="bg-white/90 text-[#2C2C2C] px-3 py-1 text-[10px] tracking-[0.2em] rounded-full" data-testid="partner-category">{partner.category.toUpperCase()}</span>
              {partner.featured && (
                <span className="text-white px-3 py-1 text-[10px] tracking-[0.2em] rounded-full inline-flex items-center gap-1" style={{ backgroundColor: '#DE92A9' }}>
                  <Star size={10} fill="white" /> FEATURED PARTNER
                </span>
              )}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mb-3" style={{ fontFamily: '"Playfair Display", serif' }} data-testid="partner-name">
              {partner.name}
            </h1>
            <p className="flex items-center gap-1.5 text-white/85 text-sm" data-testid="partner-location">
              <MapPin size={15} /> {partner.location}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-14 grid lg:grid-cols-[1fr_380px] gap-12 lg:gap-16">
        <div>
          <section className="mb-14" data-testid="partner-about">
            <p className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: '#DE92A9' }}>About</p>
            <p className="text-lg md:text-xl leading-relaxed mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>
              {partner.description}
            </p>
            {partner.story && (
              <div className="flex flex-col sm:flex-row gap-6 mb-6">
                {partner.portrait && (
                  <img src={partner.portrait} alt={`${partner.name} portrait`} className="w-full sm:w-40 h-52 object-cover rounded-xl shrink-0" loading="lazy" data-testid="partner-portrait" />
                )}
                <p className="text-base leading-relaxed" style={{ color: '#5A5A5A' }} data-testid="partner-story">{partner.story}</p>
              </div>
            )}
            {!partner.services && <p className="text-base leading-relaxed mb-5" style={{ color: '#5A5A5A' }}>{highlights.intro}</p>}
            {partner.services && <p className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: '#DE92A9' }}>Services</p>}
            <ul className="grid sm:grid-cols-2 gap-3">
              {(partner.services || highlights.points).map((pt) => (
                <li key={pt} className="flex items-start gap-2.5 text-sm" style={{ color: '#2C2C2C' }}>
                  <span className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#FBF3F5', color: '#DE92A9' }}>
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {pt}
                </li>
              ))}
            </ul>
            {partner.testimonial && (
              <blockquote className="mt-8 border-l-4 pl-6 py-4 rounded-r-xl" style={{ borderColor: '#DE92A9', backgroundColor: '#FDF2F4' }} data-testid="partner-testimonial">
                <p className="italic text-base leading-relaxed mb-2" style={{ color: '#2C2C2C' }}>"{partner.testimonial.quote}"</p>
                <footer className="text-xs tracking-[0.15em] uppercase" style={{ color: '#C9758F' }}>{partner.testimonial.author}</footer>
              </blockquote>
            )}
            <div className="flex flex-wrap gap-3 mt-8">
              {hasWebsite && (
                <a href={partner.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs tracking-[0.15em] font-semibold px-5 py-3 rounded-full border-2 transition-colors hover:bg-[#FBF3F5]" style={{ borderColor: '#DE92A9', color: '#C9758F' }} data-testid="partner-website-link">
                  <ExternalLink size={14} /> WEBSITE
                </a>
              )}
              {hasInstagram && (
                <a href={partner.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs tracking-[0.15em] font-semibold px-5 py-3 rounded-full border-2 transition-colors hover:bg-[#FBF3F5]" style={{ borderColor: '#DE92A9', color: '#C9758F' }} data-testid="partner-instagram-link">
                  <Instagram size={14} /> INSTAGRAM
                </a>
              )}
            </div>
          </section>

          <PartnerGallery images={gallery} name={partner.name} />

          {weddings.length > 0 && (
            <section className="mb-14" data-testid="partner-featured-weddings">
              <p className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: '#DE92A9' }}>As seen in</p>
              <h2 className="text-2xl md:text-3xl mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>Real weddings featuring {partner.name}</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {weddings.map((w) => (
                  <Link key={w.slug} to={`/wedding-stories/${w.slug}`} className="group flex gap-4 bg-white rounded-xl border border-[#EFE6E9] p-3 hover:shadow-lg transition-shadow" data-testid={`partner-wedding-${w.slug}`}>
                    <img src={w.image} alt={w.couple} className="w-24 h-24 object-cover rounded-lg shrink-0" loading="lazy" />
                    <div className="min-w-0 py-1">
                      <p className="text-lg leading-tight group-hover:text-[#DE92A9] transition-colors" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>{w.couple}</p>
                      <p className="text-xs mt-1 truncate" style={{ color: '#757B7F' }}>{w.venue}</p>
                      <span className="inline-flex items-center gap-1 text-[11px] tracking-[0.15em] font-semibold mt-2" style={{ color: '#DE92A9' }}>READ STORY <ArrowRight size={11} /></span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:sticky lg:top-40 self-start space-y-5">
          {(partner.phone || partner.email || partner.address) && (
            <div className="bg-white rounded-2xl border border-[#EFE6E9] p-6 text-sm space-y-2.5" data-testid="partner-contact-card">
              <p className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: '#DE92A9' }}>Contact</p>
              {partner.address && <p className="flex items-start gap-2" style={{ color: '#5A5A5A' }}><MapPin size={14} className="mt-0.5 shrink-0" style={{ color: '#DE92A9' }} />{partner.address}</p>}
              {partner.phone && <a href={`tel:${partner.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-[#DE92A9]" style={{ color: '#2C2C2C' }}><Phone size={14} style={{ color: '#DE92A9' }} />{partner.phone}</a>}
              {partner.email && <a href={`mailto:${partner.email}`} className="flex items-center gap-2 hover:text-[#DE92A9] break-all" style={{ color: '#2C2C2C' }}><Mail size={14} style={{ color: '#DE92A9' }} />{partner.email}</a>}
            </div>
          )}
          <div className="bg-white rounded-2xl border border-[#EFE6E9] shadow-xl p-7" data-testid="partner-enquiry-card">
            <p className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: '#DE92A9' }}>Get in touch</p>
            <h2 className="text-2xl mb-2" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>Enquire with {partner.name}</h2>
            <p className="text-sm mb-6" style={{ color: '#757B7F' }}>Tell them about your day and we will make the introduction.</p>
            <PartnerEnquiryForm partner={partner} slug={slug} />
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="bg-white py-16" data-testid="partner-related">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-2xl md:text-3xl" style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}>More {partner.category} partners</h2>
              <Link to="/partners" className="text-xs tracking-[0.2em] font-semibold inline-flex items-center gap-1" style={{ color: '#DE92A9' }}>VIEW ALL <ArrowRight size={12} /></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => <PartnerCard key={getPartnerSlug(p)} partner={p} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
